/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.14.0
 * @since 1.14.0
 */

import * as Yup from 'yup';

import { EFormAddressField } from '../types/formAddressTypes';

/*
* -------------------------------------------
*           Field Properties
* -------------------------------------------
*/

export const FormAddressFieldsSchema = {
    [EFormAddressField.city]: {
        dataName: 'form-address-city',
        required: true,
    },
    [EFormAddressField.planningElement]: {
        dataName: 'form-address-planningElement',
        required: true,
    },
    [EFormAddressField.planningElementOptional]: {
        dataName: 'form-address-planningElementOptional',
    },
    [EFormAddressField.street]: {
        dataName: 'form-address-street',
        required: true,
    },
    [EFormAddressField.streetOptional]: {
        dataName: 'form-address-streetOptional',
    },
    [EFormAddressField.house]: {
        dataName: 'form-address-house',
        required: true,
    },
    [EFormAddressField.houseOptional]: {
        dataName: 'form-address-houseOptional',
    },
    [EFormAddressField.block]: {
        dataName: 'form-address-block',
    },
    [EFormAddressField.apartment]: {
        dataName: 'form-address-apartment',
        required: true,
    },
    [EFormAddressField.apartmentOptional]: {
        dataName: 'form-address-apartmentOptional',
    },
    [EFormAddressField.structure]: {
        dataName: 'form-address-structure',
    },
};

/*
* -------------------------------------------
*           Field Dependence
* -------------------------------------------
*/

interface IFormAddressFieldDependence {
    clearAfterChange?: EFormAddressField[];
    dependenceOnValue: EFormAddressField[];
    disableOption?: EFormAddressField;
    requiredForChildren?: boolean;
}

const clearAfterChangeHouse = [
    EFormAddressField.apartment,
    EFormAddressField.apartmentOptional,
    EFormAddressField.block,
    EFormAddressField.structure,
];
const clearAfterChangeHouseOptional = [...clearAfterChangeHouse, EFormAddressField.house];

const clearAfterChangeStreet = [
    ...clearAfterChangeHouse,
    EFormAddressField.house,
    EFormAddressField.houseOptional,
];
const clearAfterChangeStreetOptional = [...clearAfterChangeStreet, EFormAddressField.street];

const clearAfterChangePlaningElement = [
    ...clearAfterChangeStreet,
    EFormAddressField.street,
    EFormAddressField.streetOptional,
];
const clearAfterChangePlaningOptionalElement = [...clearAfterChangePlaningElement, EFormAddressField.planningElement];

const clearAfterChangeCity = [
    ...clearAfterChangePlaningElement,
    EFormAddressField.planningElement,
];

export const FormAddressFieldsDependence: { [key in EFormAddressField]: IFormAddressFieldDependence } = {
    [EFormAddressField.city]: {
        clearAfterChange: clearAfterChangeCity,
        dependenceOnValue: [],
    },
    [EFormAddressField.planningElement]: {
        clearAfterChange: clearAfterChangePlaningElement,
        dependenceOnValue: [EFormAddressField.city],
        disableOption: EFormAddressField.planningElementOptional,
    },
    [EFormAddressField.planningElementOptional]: {
        clearAfterChange: clearAfterChangePlaningOptionalElement,
        dependenceOnValue: [EFormAddressField.city],
    },
    [EFormAddressField.street]: {
        clearAfterChange: clearAfterChangeStreet,
        dependenceOnValue: [EFormAddressField.city, EFormAddressField.planningElement],
        disableOption: EFormAddressField.streetOptional,
    },
    [EFormAddressField.streetOptional]: {
        clearAfterChange: clearAfterChangeStreetOptional,
        dependenceOnValue: [EFormAddressField.city, EFormAddressField.planningElement],
    },
    [EFormAddressField.house]: {
        clearAfterChange: clearAfterChangeHouse,
        dependenceOnValue: [EFormAddressField.city, EFormAddressField.street, EFormAddressField.planningElement],
        disableOption: EFormAddressField.houseOptional,
        requiredForChildren: true,
    },
    [EFormAddressField.houseOptional]: {
        clearAfterChange: clearAfterChangeHouseOptional,
        dependenceOnValue: [EFormAddressField.city, EFormAddressField.street, EFormAddressField.planningElement],
    },
    [EFormAddressField.block]: {
        dependenceOnValue: [EFormAddressField.house],
    },
    [EFormAddressField.apartment]: {
        dependenceOnValue: [EFormAddressField.house],
        disableOption: EFormAddressField.apartmentOptional,
    },
    [EFormAddressField.apartmentOptional]: {
        clearAfterChange: [EFormAddressField.apartment],
        dependenceOnValue: [EFormAddressField.house],
    },
    [EFormAddressField.structure]: {
        dependenceOnValue: [EFormAddressField.house],
    },
};


/*
* -------------------------------------------
*           Formik validation
* -------------------------------------------
*/


// const formAddressTest = Yup.object().test('form-address', 'form-address incorrect', (value) => !!value?.id);
const addressFieldRequires = Yup.object().shape({
    def: Yup.string().required(),
    id: Yup.string().required(),
});

const addressField = (field: EFormAddressField[]) => Yup.object().when(field, {
    is: (...options: boolean[]) => !options.includes(true),
    then: addressFieldRequires,
    otherwise: Yup.object().nullable(true),
});

const addressFieldHouseRequires = Yup.object().shape({
    def: Yup.string().required(),
});

const addressFieldHouse = (field: EFormAddressField[]) => Yup.object().when(field, {
    is: (...options: boolean[]) => !options.includes(true),
    then: addressFieldHouseRequires,
    otherwise: Yup.object().nullable(true),
});

export const FormAddressValidationSchema = Yup.object().shape({
    [EFormAddressField.city]: addressFieldRequires,
    [EFormAddressField.planningElement]: addressField([EFormAddressField.planningElementOptional]),
    [EFormAddressField.planningElementOptional]: Yup.boolean().nullable(true),
    [EFormAddressField.street]: addressField([EFormAddressField.streetOptional]),
    [EFormAddressField.streetOptional]: Yup.boolean().nullable(true),
    [EFormAddressField.house]: addressFieldHouse([EFormAddressField.houseOptional]),
    [EFormAddressField.houseOptional]: Yup.boolean().nullable(true),
    [EFormAddressField.block]: Yup.object().nullable(true),
    [EFormAddressField.apartment]:
        addressFieldHouse([EFormAddressField.houseOptional, EFormAddressField.apartmentOptional]),
    [EFormAddressField.apartmentOptional]: Yup.boolean().nullable(true),
    [EFormAddressField.structure]: Yup.object().nullable(true),
});
